// PDFViewer.tsx
import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Form,
  Row,
  Badge,
  OverlayTrigger,
  Tooltip,
  Alert,
} from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import DocumentsTable from "./DocumentTable";
import {
  ftoGeneration,
  getEntity,
  startScraping,
  updateEntity,
  uploadPDF,
  getFtoReport,
} from "../services/document";
import "bootstrap/dist/css/bootstrap.min.css";
import { EntityType, entityTypes } from "../utils/types";
import { ENTITY_COLORS, ENTITY_BORDER_COLORS } from "../utils/constant";
import { getCustomEntities } from "../services/customentity";
import EntityLegend from "./EntityLegend";
import PatentOfficeSelector from "./PatentOfficeSelector";
import "../styles/pdfViewer.css";
import EntityManagement from "./EntityManagement";
import EntityStatistics from "./EntityStatistics";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export interface AnnotationPosition {
  pageNumber: number;
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Annotation {
  id: string;
  text: string;
  entity: EntityType;
  position: AnnotationPosition;
}

interface PDFViewerProps {
  annotations: Annotation[];
  onAnnotationCreate: (annotation: Annotation) => void;
  onAnnotationDelete: (id: string) => void;
  onAnnotationReset: () => void;
}

// annotate svg
const SvgAnotate = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="me-1"
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
);

const SvgRemove = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="me-1"
  >
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"></path>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

const PDF_VIEWER_HEIGHT = 700; // Fixed height for PDF viewer

export const downloadFromPublicGCS = async (
  docId: string,
  publicUrl: string
): Promise<void> => {
  try {
    // let gcsFilePath =
    //   "gs://fyled-invention-docs/71859f1f-cf6d-47ff-b7d2-a639a50067ce-fto-report.pdf";
    // Validate the input
    // if (!gcsFilePath.startsWith("gs://")) {
    //   throw new Error('Invalid GCS file path. Must start with "gs://"');
    // }

    // // Parse the GCS path to extract bucket name and file path
    // const gsPath = gcsFilePath.replace("gs://", "");
    // const [bucketName, ...pathParts] = gsPath.split("/");
    // const objectPath = pathParts.join("/");

    // if (!bucketName || !objectPath) {
    //   throw new Error(
    //     "Invalid GCS path format. Expected format: gs://bucket-name/path/to/file"
    //   );
    // }

    // // Use the last part of the path as the filename if saveAsFilename not provided
    // const defaultFilename = objectPath.split("/").pop() || "download";
    const filename = `fto-search-report-${docId}.pdf`;

    // Construct the public URL
    // const publicUrl = `https://storage.googleapis.com/${bucketName}/${encodeURIComponent(
    //   objectPath
    // )}`;

    // Fetch the file from the public URL
    const response = await fetch(publicUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to download file: ${response.status} ${response.statusText}`
      );
    }

    // Get file as blob
    const fileBlob = await response.blob();

    // Create a URL for the blob
    const url = window.URL.createObjectURL(fileBlob);

    // Create a temporary anchor element and trigger download
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);
  } catch (error) {
    console.error("Error downloading file from GCS:", error);
    throw error;
  }
};

const PDFViewer: React.FC<PDFViewerProps> = ({
  annotations,
  onAnnotationCreate,
  onAnnotationDelete,
  onAnnotationReset,
}) => {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedEntity, setSelectedEntity] = useState<EntityType>("GENE");
  const [scale, setScale] = useState(1);
  const [isDeAnnotateMode, setIsDeAnnotateMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );
  const [version, setVersion] = useState(0);
  const [docId, setDocumentId] = useState("");
  const [docStatus, setDocStatus] = useState("");

  // Updated state variables for entity management
  const [allEntityTypes, setAllEntityTypes] = useState<string[]>([
    ...entityTypes,
  ]);
  const [currentEntityTypes, setCurrentEntityTypes] = useState<string[]>([
    ...entityTypes,
  ]);
  const [customEntitiesVersion, setCustomEntitiesVersion] = useState(0);

  // Load custom entities on component mount
  useEffect(() => {
    const loadCustomEntities = async () => {
      try {
        const customEntities = await getCustomEntities();

        // Update colors and border colors
        customEntities.forEach((entity: any) => {
          ENTITY_COLORS[entity.entity_tag as EntityType] =
            entity.metadata.color;
          ENTITY_BORDER_COLORS[entity.entity_tag as EntityType] =
            entity.metadata.borderColor;
        });

        // Update all entity types
        const updatedEntityTypes = [
          ...entityTypes,
          ...customEntities.map((entity: any) => entity.entity_tag),
        ];

        setAllEntityTypes(updatedEntityTypes);
        setCurrentEntityTypes(updatedEntityTypes);
      } catch (error) {
        console.error("Error loading custom entities:", error);
      }
    };

    loadCustomEntities();
  }, []);

  // Handler for when entities are updated from EntityManagement
  const handleEntitiesUpdated = (
    allEntities: string[],
    customEntities: any[]
  ) => {
    setAllEntityTypes(allEntities);
    setCurrentEntityTypes(allEntities);
    setCustomEntitiesVersion((prev) => prev + 1);

    // This ensures the colors are updated throughout the application
    customEntities.forEach((entity) => {
      ENTITY_COLORS[entity.entity_tag as EntityType] = entity.metadata.color;
      ENTITY_BORDER_COLORS[entity.entity_tag as EntityType] =
        entity.metadata.borderColor;
    });
  };

  // Reset file input when changing PDF
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === "application/pdf") {
        setIsLoading(true);
        setError(null);
        setPdfFile(file);
        await uploadPDF(file);
        // Reset to page 1 when loading a new file
        setIsLoading(false);
      } else {
        setError("Please upload a valid PDF file");
      }
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  const handleDocumentLoadError = (error: Error) => {
    setIsLoading(false);
    setError(`Error loading PDF: ${error.message}`);
  };

  const findOverlappingAnnotations = (selectionRect: DOMRect) => {
    const container = containerRef.current;
    if (!container) return [];

    const containerRect = container.getBoundingClientRect();

    // Get scroll position
    const scrollTop = container.scrollTop;
    const scrollLeft = container.scrollLeft;

    return annotations.filter((annotation) => {
      if (annotation.position.pageNumber !== currentPage) return false;

      // Convert annotation coordinates to screen coordinates, accounting for scroll
      const annotationRect = {
        top: annotation.position.top * scale + containerRect.top - scrollTop,
        left:
          annotation.position.left * scale + containerRect.left - scrollLeft,
        right:
          annotation.position.left * scale +
          annotation.position.width * scale +
          containerRect.left -
          scrollLeft,
        bottom:
          annotation.position.top * scale +
          annotation.position.height * scale +
          containerRect.top -
          scrollTop,
      };

      // Check for overlap
      return !(
        selectionRect.right < annotationRect.left ||
        selectionRect.left > annotationRect.right ||
        selectionRect.bottom < annotationRect.top ||
        selectionRect.top > annotationRect.bottom
      );
    });
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const text = selection.toString().trim();

    if (text) {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const rangeRect = range.getBoundingClientRect();

      // Get the container's scroll position
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;

      if (isDeAnnotateMode) {
        // In de-annotate mode, find and remove overlapping annotations
        const overlappingAnnotations = findOverlappingAnnotations(rangeRect);

        if (overlappingAnnotations.length > 0) {
          overlappingAnnotations.forEach((annotation) => {
            onAnnotationDelete(annotation.id);
          });
        }
      } else {
        // In annotate mode, create new annotation
        // Adjust position calculations to account for scroll position
        const annotation: Annotation = {
          id: crypto.randomUUID(),
          text,
          entity: selectedEntity as EntityType,
          position: {
            pageNumber: currentPage,
            // Add scroll offsets to get accurate positions relative to the document
            top: (rangeRect.top - containerRect.top + scrollTop) / scale,
            left: (rangeRect.left - containerRect.left + scrollLeft) / scale,
            width: rangeRect.width / scale,
            height: rangeRect.height / scale,
          },
        };

        onAnnotationCreate(annotation);
      }
      selection.removeAllRanges();
    }
  };

  // Handle click on annotation for removal in de-annotate mode
  const handleAnnotationClick = (annotationId: string) => {
    if (isDeAnnotateMode) {
      onAnnotationDelete(annotationId);
    }
  };

  const currentPageAnnotations = annotations.filter(
    (annotation) => annotation.position.pageNumber === currentPage
  );

  // Function to change page with boundary checks
  const changePage = (delta: number) => {
    const newPage = currentPage + delta;
    if (newPage >= 1 && newPage <= numPages) {
      setCurrentPage(newPage);
    }
  };

  const handleDocumentSelect = async (
    document_path: string,
    documentId: string,
    status: string
  ) => {
    const splits = document_path.split("/").splice(2);
    onAnnotationReset();

    const documentPath = `https://storage.googleapis.com/${splits.join("/")}`;
    setSelectedDocumentId(documentPath);
    setDocumentId(documentId);
    setDocStatus(status);

    const response = await getEntity(documentId);
    if (response && response.entities) {
      response.entities.forEach((item: any) => {
        onAnnotationCreate({ ...item, id: crypto.randomUUID() });
      });
      setVersion(response.version);
    }

    // In a real implementation, you would fetch the PDF file from the server
    // using the document ID and then load it
    // For demonstration purposes, we'll simulate loading a document
    setIsLoading(true);
    setError(null);

    // Simulate an API call to get the document
    setTimeout(() => {
      // This would be replaced with actual API response handling
      setIsLoading(false);
      setCurrentPage(1);
    }, 1000);
  };

  // Handle entity selection from EntityManagement component
  const handleEntitySelect = (entity: string) => {
    setSelectedEntity(entity as EntityType);
  };

  // Save annotations
  const handleSaveAnnotations = async () => {
    try {
      await updateEntity(docId, version + 1, annotations);
      // Could add success message here
    } catch (error) {
      setError("Failed to save annotations");
    }
  };

  // start scraping
  const handleScraping = async () => {
    try {
      await startScraping(docId);
      window.location.reload();
    } catch (error) {
      setError("Failed to start scraping");
    }
  };

  const handleFtoGenerate = async () => {
    try {
      await ftoGeneration(docId);
    } catch (error) {
      setError("Failed to generate fto");
    }
  };

  const handleFtoDownload = async () => {
    try {
      const res = await getFtoReport(docId);
      const publicUrl = res.metadata.gcs_url;
      console.log(publicUrl);
      await downloadFromPublicGCS(docId, publicUrl);
    } catch (error) {
      setError("Failed to generate fto");
    }
  };

  return (
    <Container fluid className="py-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="application/pdf"
        style={{ display: "none" }}
      />

      <DocumentsTable onSelectDocument={handleDocumentSelect} />

      {selectedDocumentId && (
        <Row>
          <Col xl={9} lg={8} className="mb-4">
            <Card className="shadow-sm border-0 h-100">
              {/* Card.Header section with improved responsiveness */}
              <Card.Header className="bg-white border-bottom py-3">
                <div className="pdf-header-container">
                  {/* Action buttons - left side */}
                  <div className="pdf-action-buttons">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveAnnotations}
                      className="text-nowrap"
                    >
                      Save Annotation
                    </Button>

                    {docStatus === "ENTITY_EXTRACTED" && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleScraping}
                        className="text-nowrap"
                      >
                        Start Scraping
                      </Button>
                    )}

                    {docStatus === "PATENTS_REVIEW" && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={handleFtoGenerate}
                        className="text-nowrap"
                      >
                        FTO Generation
                      </Button>
                    )}

                    {docStatus === "DONE" && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleFtoDownload}
                        className="text-nowrap"
                      >
                        Download FTO
                      </Button>
                    )}
                    {/* Patent Office Selector */}
                    {docStatus === "ENTITY_EXTRACTED" && (
                      <PatentOfficeSelector documentId={docId} />
                    )}
                  </div>

                  {/* Navigation Controls - right side */}
                  <div className="pdf-nav-controls">
                    {/* Page Navigation */}
                    <ButtonGroup aria-label="Page navigation">
                      <Button
                        variant="outline-primary"
                        onClick={() => changePage(-1)}
                        disabled={currentPage <= 1 || isLoading}
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "31px", width: "31px", padding: 0 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                      </Button>

                      <Button
                        variant="outline-primary"
                        disabled
                        className="page-counter"
                        style={{
                          height: "31px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {currentPage}/{numPages}
                      </Button>

                      <Button
                        variant="outline-primary"
                        onClick={() => changePage(1)}
                        disabled={currentPage >= numPages || isLoading}
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "31px", width: "31px", padding: 0 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </Button>
                    </ButtonGroup>

                    {/* Zoom Controls */}
                    <ButtonGroup aria-label="Zoom controls">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
                        disabled={isLoading}
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "31px", width: "31px", padding: 0 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </Button>

                      <Button
                        variant="outline-secondary"
                        disabled
                        className="zoom-label"
                        style={{ height: "31px", width: "50px" }}
                      >
                        {Math.round(scale * 100)}%
                      </Button>

                      <Button
                        variant="outline-secondary"
                        onClick={() => setScale((s) => Math.min(2.5, s + 0.1))}
                        disabled={isLoading}
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "31px", width: "31px", padding: 0 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="ms-3">Loading PDF...</span>
                  </div>
                ) : (
                  <>
                    {error ? (
                      <Alert variant="danger" className="m-4">
                        {error}
                      </Alert>
                    ) : (
                      <div
                        ref={containerRef}
                        className={`position-relative ${
                          isDeAnnotateMode ? "cursor-crosshair" : "cursor-text"
                        }`}
                        onMouseUp={handleTextSelection}
                        style={{
                          backgroundColor: "#f5f5f5",
                          height: `${PDF_VIEWER_HEIGHT}px`,
                          overflowY: "auto",
                        }}
                      >
                        <Document
                          file={selectedDocumentId}
                          onLoadSuccess={handleDocumentLoadSuccess}
                          onLoadError={handleDocumentLoadError}
                          loading={
                            <div className="d-flex justify-content-center align-items-center py-5">
                              <div
                                className="spinner-border text-primary"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          }
                        >
                          <Page
                            pageNumber={currentPage}
                            renderTextLayer
                            scale={scale}
                            loading={
                              <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ height: "600px" }}
                              >
                                <div
                                  className="spinner-border text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              </div>
                            }
                          />
                          {currentPageAnnotations.map((annotation) => (
                            <OverlayTrigger
                              key={annotation.id}
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {annotation.text}{" "}
                                  <Badge bg="secondary">
                                    {annotation.entity}
                                  </Badge>
                                  {isDeAnnotateMode && (
                                    <div className="text-danger mt-1">
                                      (Click to remove)
                                    </div>
                                  )}
                                </Tooltip>
                              }
                            >
                              <div
                                className="position-absolute"
                                style={{
                                  top: annotation.position.top * scale,
                                  left: annotation.position.left * scale,
                                  width: annotation.position.width * scale,
                                  height: annotation.position.height * scale,
                                  backgroundColor:
                                    ENTITY_COLORS[annotation.entity] ||
                                    "rgba(211, 211, 211, 0.5)",
                                  border: `2px solid ${
                                    ENTITY_BORDER_COLORS[annotation.entity] ||
                                    "rgba(128, 128, 128, 0.8)"
                                  }`,
                                  borderRadius: "4px",
                                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                                  zIndex: 100,
                                  cursor: isDeAnnotateMode ? "pointer" : "help",
                                }}
                                onClick={() =>
                                  handleAnnotationClick(annotation.id)
                                }
                              />
                            </OverlayTrigger>
                          ))}
                        </Document>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} lg={4}>
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-white border-bottom py-3">
                <h5 className="mb-0">Annotation Controls</h5>
              </Card.Header>

              <Card.Body>
                <h6 className="mb-3">Mode</h6>
                <ButtonGroup className="d-flex mb-4">
                  <Button
                    variant={
                      !isDeAnnotateMode ? "primary" : "outline-secondary"
                    }
                    onClick={() => setIsDeAnnotateMode(false)}
                    className="w-50"
                    disabled={isLoading}
                  >
                    {SvgAnotate}
                    Annotate
                  </Button>
                  <Button
                    variant={isDeAnnotateMode ? "danger" : "outline-secondary"}
                    onClick={() => setIsDeAnnotateMode(true)}
                    className="w-50"
                    disabled={isLoading}
                  >
                    {SvgRemove}
                    Remove
                  </Button>
                </ButtonGroup>

                {isDeAnnotateMode ? (
                  <Alert variant="info" className="mb-4">
                    <small>
                      <strong>Remove Mode Active:</strong> Select text or click
                      directly on annotations to remove them.
                    </small>
                  </Alert>
                ) : (
                  <>
                    <h6 className="mb-3">Entity Type</h6>
                    <Form.Select
                      value={selectedEntity}
                      onChange={(e) =>
                        setSelectedEntity(e.target.value as EntityType)
                      }
                      className="mb-4"
                      disabled={isLoading}
                    >
                      {currentEntityTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                )}

                {/* Use Entity Statistics Component */}
                <EntityStatistics
                  annotations={annotations}
                  currentPageAnnotations={currentPageAnnotations}
                  entityTypes={currentEntityTypes}
                />
              </Card.Body>
            </Card>

            {/* Entity Management Component */}
            <EntityManagement
              onSelectEntity={handleEntitySelect}
              selectedEntity={selectedEntity}
              onEntitiesUpdated={handleEntitiesUpdated}
            />

            {/* Entity Legend Component */}
            <EntityLegend
              entityTypes={currentEntityTypes}
              onSelectEntity={handleEntitySelect}
              isDeAnnotateMode={isDeAnnotateMode}
              ENTITY_COLORS={ENTITY_COLORS}
              ENTITY_BORDER_COLORS={ENTITY_BORDER_COLORS}
              customEntitiesVersion={customEntitiesVersion}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PDFViewer;
